export function convertSecondsToHoursAndMinutes(durationInSeconds: number): string {
  const durationInMinutes = durationInSeconds / 60;
  return convertMinutesToHoursAndMinutes(durationInMinutes);
}

export function convertMinutesToHoursAndMinutes(durationInMinutes: number): string {
  if (durationInMinutes <= 0) return '-';

  const hours = Math.floor(durationInMinutes / 60);
  const minutes = Math.floor(durationInMinutes % 60);

  const hoursText = hours >= 10 ? `${hours}` : hours > 0 ? `0${hours}` : '00';
  const minutesText = minutes >= 10 ? `${minutes}` : minutes > 0 ? `0${minutes}` : '00';

  return `${hoursText}:${minutesText}`;
}

export function getMinutes(durationInMinutes: number): string {
  if (durationInMinutes === 0 || Number.isNaN(durationInMinutes) || durationInMinutes === undefined) return '-';

  const value = Math.round(durationInMinutes);

  return value > 1 || value < 1 ? `${value} mins` : `${value} min`;
}

// Distinct from getMinutes' '-' (zero / no data): an em-dash marks a metric the backend
// deliberately suppressed to null under an active CPT filter (no-cpt room-interval / adjacency
// metrics that have no defensible per-code attribution). Kept separate so a suppressed cell reads
// differently from an empty one. See CPT_CODE_CAPTURE_AND_GROUPING.md ("Per-metric CPT-conditioning
// classes").
export const SUPPRESSED_METRIC = '—';

// Null-safe minutes formatter for CPT-suppressible columns. `getMinutes(null)` returns '0 mins'
// (null slips past its 0/NaN/undefined guards and Math.round(null) === 0), which would misrepresent
// a suppressed value as zero; this renders the em-dash instead. Only `null` is the backend's
// suppression signal under an active CPT filter — `undefined` (an absent / empty totals row) and
// every real value fall straight through to getMinutes, so the no-CPT rendering path is untouched.
export function getMinutesOrDash(durationInMinutes: number | null | undefined): string {
  return durationInMinutes === null ? SUPPRESSED_METRIC : getMinutes(durationInMinutes as number);
}

// Null-safe percentage formatter for CPT-suppressible percentage columns (e.g. physician
// pipoOverE2EPercentage). `null` (backend suppression under an active CPT filter) renders the
// em-dash; `undefined` (absent / empty totals row) keeps the pre-existing '-' empty convention; a
// real fraction renders as the existing `${(fraction * 100).toFixed(2)}%`.
export function formatPercentOrDash(fraction: number | null | undefined): string {
  if (fraction === null) return SUPPRESSED_METRIC;
  return fraction === undefined ? '-' : `${(fraction * 100).toFixed(2)}%`;
}
